import React, { Fragment, useState } from 'react';
import { Modal, InputNumber, Button, Space, App, Flex, Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { InventoryLocationItem, LocationLockEnum, WLocation } from '@/types/inventoryV2';
import { LocationSelect } from '@/components/Selector';
import ProductDesc from '@/components/Desc/ProductDesc';
import BarcodeEditModal from '../InboundOrderForm/BarcodeEditModal';
import { BarcodeOutlined } from '@ant-design/icons';
import SText from '@/styles/SText';
import { moveLocation } from '@/apis/inventoryv2';
import { getLocationIdx } from '@/utils/wLocation';

interface MoveLocationFormProps {
  locItem: InventoryLocationItem;
  onClose: () => void;
  onOk: () => void;
}

const MoveLocationForm: React.FC<MoveLocationFormProps> = ({ locItem, onClose, onOk }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [selectLocationOpen, setSelectLocationOpen] = useState(false);
  const [targetLocation, setTargetLocation] = useState<WLocation | null>(null);
  const [count, setCount] = useState<number>(0);
  const [barcodes, setBarcodes] = useState<string[]>([]);
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const maxCount = (locItem?.count || 0) - (locItem?.preOut || 0);
  const sourceId = locItem?.location?.id ?? '';
  const lotStr = locItem?.lotItem?.lot?.lot || '';
  const productCode = locItem?.lotItem?.product?.code || '';
  const product = locItem?.lotItem?.product || null;

  const handleLocationSelect = (location: WLocation) => {
    if (location.id === sourceId) return notification.error({ placement: 'top', message: t('warehouse.moveFail'), description: t('warehouse.moveCurTip') })
    if (location.lockState === LocationLockEnum.LOCKED) return notification.error({ placement: 'top', message: t('warehouse.moveFail'), description: t('warehouse.locationLocked') })
    setTargetLocation(location);
    setSelectLocationOpen(false);
  };

  const handleBarcodeEdit = (newBarcodes: string[]) => {
    if (newBarcodes.length > count) {
      notification.warning({
        placement: 'top',
        message: t('warehouse.checkBarcodes'),
        description: t('warehouse.barcodesExceedNewCount')
      });
      return;
    }
    setBarcodes(newBarcodes);
    setBarcodeModalOpen(false);
  };

  const handleCountChange = (value: number | null) => {
    const newCount = value || 0;
    setCount(newCount);

    if (barcodes.length > newCount) {
      notification.warning({
        placement: 'top',
        message: t('warehouse.checkBarcodes'),
        description: t('warehouse.barcodesExceedNewCount')
      });
      setBarcodes(barcodes.slice(0, newCount));
    }
  };

  const handleOk = () => {
    if (!targetLocation) {
      notification.error({
        placement: 'top',
        message: t('warehouse.pleaseSelectLocation')
      });
      return;
    }

    if (count <= 0) {
      notification.error({
        placement: 'top',
        message: t('warehouse.pleaseInputCount')
      });
      return;
    }

    if (count > maxCount) {
      notification.error({
        placement: 'top',
        message: t('warehouse.countExceedMax')
      });
      return;
    }
    setLoading(true);
    moveLocation(sourceId, targetLocation.id, [
      {
        lotStr,
        productCode,
        barcodes,
        count,
      }
    ])
      .then(() => {
        onOk();
      })
      .catch((err: any) => {
        if (err?.code === 73007 || err?.code === 73008) {
          notification.error({
            placement: 'top',
            message: t('warehouse.moveFail'),
            description: t('warehouse.insufficientInventory') + `: ${productCode}`
          })
        } else if (err === 72906) {
          notification.error({
            message: t('inventory.locLocked')
          })
        } else if (err === 72913) {
          notification.error({
            message: t('inventory.preOutExsist')
          })
        } else {
          notification.error({
            placement: 'top',
            message: t('warehouse.moveFail'),
            description: err + ''
          })
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal okButtonProps={{ loading }}
      title={t('warehouse.moveLoc')}
      open={true}
      onCancel={onClose}
      onOk={handleOk}
      width={800}
      destroyOnClose
    >
      <Flex vertical gap={16}>
        <Space style={{ width: '100%' }} direction="vertical" size="small">
          <Typography.Title level={4}>{t('warehouse.targetLocation')}</Typography.Title>
          <Flex>
            <span>{t('warehouse.selectLocation')}：</span>
            <Input value={getLocationIdx(targetLocation)} readOnly onClick={() => setSelectLocationOpen(true)} size='small' style={{ flex: 1 }} />
          </Flex>

        </Space>

        {product && (
          <Fragment>
            <Typography.Title level={4}>{t('purchase.productInfo')}</Typography.Title>
            <ProductDesc product={product} />
          </Fragment>
        )}

        <Flex gap={16} align="center">
          <Space>
            <span>{t('warehouse.moveCount')}:</span>
            <InputNumber changeOnWheel
              min={0}
              max={maxCount}
              value={count}
              onChange={handleCountChange}
              style={{ width: 120 }}
            />
          </Space>

          <Space>
            <span>{t('warehouse.maxMoveCount')}:</span>
            <SText text={String(maxCount)} />
          </Space>

          <Button
            icon={<BarcodeOutlined />}
            onClick={() => setBarcodeModalOpen(true)}
          >
            {t('warehouse.editBarcodes')} ({barcodes.length})
          </Button>
        </Flex>
      </Flex>

      <LocationSelect
        open={selectLocationOpen}
        onCancel={() => setSelectLocationOpen(false)}
        onChange={handleLocationSelect}
        value={targetLocation?.id}
      />

      <BarcodeEditModal
        open={barcodeModalOpen}
        initialBarcodes={barcodes}
        createNum={count}
        onOk={handleBarcodeEdit}
        onCancel={() => setBarcodeModalOpen(false)}
      />
    </Modal>
  );
};

export default MoveLocationForm;